import * as WebBrowser from 'expo-web-browser';
import { Linking, Platform } from 'react-native';

import { colors } from '@/constants/theme';

const isWebUrl = (url: string) => /^https?:\/\//i.test(url);

/**
 * Open a server-supplied web URL safely.
 * - Scheme allow-list: only http(s). Server schemas accept any URI (including
 *   `javascript:`, `tel:`, `mailto:`), so the client gates before
 *   `Linking.openURL`.
 * - Failures are ignored (no installed handler, malformed URL): these are
 *   references (a provider page, tracking, a brief), and failing to open one
 *   shouldn't surface a full ErrorState.
 */
export function openWebUrl(url: string): void {
  if (!isWebUrl(url)) return;
  Linking.openURL(url).catch(() => {});
}

/**
 * For a URL that has to be fetched first (a short-lived signed link). Call
 * this INSIDE the tap handler, before any await, then hand the URL to the
 * function it returns. On web that reserves the new tab while the browser
 * still counts the tap as a user gesture; opening after the fetch is blocked
 * as a pop-up. On a phone it opens in the in-app browser sheet, over the
 * screen the user tapped from. The returned function resolves true only when
 * the URL opened (on iOS, once the sheet is closed), and with no URL it just
 * releases the reserved tab. The opened page gets no `window.opener`.
 */
export function reserveWebUrl(): (url: string | null) => Promise<boolean> {
  const tab = Platform.OS === 'web' ? window.open('', '_blank') : null;
  return async (url) => {
    if (!url || !isWebUrl(url)) {
      tab?.close();
      return false;
    }
    if (Platform.OS === 'web') {
      if (!tab) return false;
      // Cut the new page's handle back to ours before it loads (reverse tabnabbing).
      tab.opener = null;
      tab.location.href = url;
      return true;
    }
    return WebBrowser.openBrowserAsync(url, {
      presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
      controlsColor: colors.deepGreen,
    }).then(
      () => true,
      () => false,
    );
  };
}
